import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import Tree from 'react-d3-tree';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSubscriptionStatus } from '../hooks/useSubscriptionStatus';
import { useTheme } from '../contexts/ThemeContext';
import { topics } from './Sidebar';

// Chapter descriptions for tooltips
const chapterDescriptions = {
  'arrays-and-hashing': 'Master fundamental data structures like arrays, sets, maps, and hashing techniques for efficient lookups',
  'two-pointer': 'Learn the two-pointer technique for solving array and string problems efficiently in O(n) time',
  'binary-search': 'Understand binary search algorithm and its applications for searching in sorted arrays',
  'sliding-window': 'Master the sliding window technique for optimizing substring and subarray problems',
  'parenthesis': 'Learn to handle parentheses validation, matching, and stack-based expression parsing',
  'linked-list': 'Understand linked list operations, reversal, merging, and cycle detection techniques',
  'tree': 'Master binary trees, BSTs, traversals, and fundamental tree algorithms and operations',
  'trie': 'Learn prefix trees for efficient string searching, autocomplete, and word validation',
  'heap': 'Understand priority queues, heap operations, and solving top-K and median problems',
  'interval': 'Master interval merging, overlapping detection, and scheduling optimization problems',
  'greedy': 'Learn greedy algorithms for optimization problems and making locally optimal choices',
  'graph': 'Understand graph representations, BFS, DFS, topological sort, and shortest path algorithms',
  'dynamic-programming': 'Master dynamic programming patterns, memoization, and optimization techniques',
  'backtracking': 'Learn systematic exploration of solution spaces and constraint satisfaction problems',
  'monotonic-stack': 'Understand monotonic data structures for next/previous greater element problems',
  'prefix-sum': 'Master prefix sum arrays for efficient range sum queries and subarray problems',
  'sorted-list': 'Learn sorted container operations and binary search tree applications',
  'segment-tree': 'Understand advanced tree structures for range queries and updates',
  'divide-and-conquer': 'Master the divide and conquer paradigm for solving complex recursive problems',
  'palindrome': 'Learn palindrome detection, expansion, and dynamic programming applications'
};

// Custom Node Component
const CustomNode = ({ nodeDatum, navigate, user, hasActiveSubscription, isCompact = false, completedChapters = {}, onTooltip, isDarkMode = false }) => {
  const isRootNode = !nodeDatum.parent;

  // Map tree node names to actual course IDs
  const getChapterId = (name) => {
    const mapping = {
      'Arrays & Hashing': 'arrays-and-hashing',
      'Two Pointers': 'two-pointer', // Note: singular in actual chapters
      'Binary Search': 'binary-search',
      'Sliding Window': 'sliding-window',
      'Stack': 'parenthesis', // Map to parenthesis chapter
      'Linked List': 'linked-list',
      'Trees': 'tree', // Note: singular in actual chapters
      'Trie': 'trie', // Note: singular in actual chapters
      'Heap': 'heap',
      'Intervals': 'interval', // Note: singular in actual chapters
      'Greedy': 'greedy',
      'Graphs': 'graph', // Note: singular in actual chapters
      'Dynamic Programming': 'dynamic-programming',
      'DP': 'dynamic-programming',
      'Backtracking': 'backtracking',
      'Monotonic Stack': 'monotonic-stack',
      'Mono Stack': 'monotonic-stack',
      'Advanced Patterns': 'prefix-sum', // Map to prefix-sum as representative
      'Prefix Sum': 'prefix-sum',
      'Sorted List': 'sorted-list',
      'Segment Tree': 'segment-tree',
      'Divide and Conquer': 'divide-and-conquer',
      'Palindromes': 'palindrome'
    };
    return mapping[name];
  };

  // Check if this is a premium chapter and user doesn't have access
  const chapterId = getChapterId(nodeDatum.name);
  const topicData = topics.find(topic => topic.id === chapterId);
  const isPremiumChapter = topicData ? topicData.premium : true; // Default to premium if not found
  const isPremiumLocked = isPremiumChapter && (!user || !hasActiveSubscription);
  
  // Check if this chapter is completed
  const isCompleted = chapterId && completedChapters[chapterId];
  
  // Get chapter description for tooltip
  const description = chapterId ? chapterDescriptions[chapterId] : null;

  const handleClick = () => {
    const chapterId = getChapterId(nodeDatum.name);
    if (!chapterId) return;

    // If it's a premium chapter and user doesn't have access, redirect to purchase
    if (isPremiumLocked) {
      navigate('/purchase');
      return;
    }
    
    // Navigate to the course chapter
    navigate(`/courses#${chapterId}`);
  };

  const handleMouseEnter = (event) => {
    if (description && onTooltip) {
      const rect = event.target.getBoundingClientRect();
      onTooltip({
        visible: true,
        content: description,
        title: nodeDatum.name,
        x: rect.left + rect.width / 2,
        y: rect.top - 10,
        isCompleted,
        isPremiumLocked
      });
    }
  };

  const handleMouseLeave = () => {
    if (onTooltip) {
      onTooltip({ visible: false });
    }
  };

  const cardWidth = isCompact ? 116 : 160;
  const cardHeight = isCompact ? 42 : 54;
  const maxChars = isCompact ? 16 : 21;
  const displayName = nodeDatum.name.length > maxChars ? `${nodeDatum.name.slice(0, maxChars - 3)}...` : nodeDatum.name;
  const accentColor = isCompleted
    ? (isDarkMode ? '#4ade80' : '#15905f')
    : isPremiumLocked
      ? (isDarkMode ? '#f7c66b' : '#b7791f')
      : (isDarkMode ? '#c7d1ff' : '#64748b');
  const statusLabel = isCompleted ? 'done' : isPremiumLocked ? 'pro' : '';
  const statusColor = isCompleted
    ? (isDarkMode ? '#86efac' : '#15905f')
    : (isDarkMode ? '#d8b66b' : '#9a6a1b');
  const nodeFill = isDarkMode
    ? (isRootNode ? 'rgba(18,25,41,0.96)' : 'rgba(18,25,41,0.82)')
    : (isRootNode ? 'rgba(255,255,255,0.94)' : 'rgba(255,255,255,0.78)');
  const textFill = isDarkMode ? '#f8fafc' : '#080d18';
  
  return (
    <g 
      onClick={handleClick} 
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{ 
        cursor: 'pointer',
        transition: 'all 0.3s ease'
      }}
      className="custom-tree-node"
    >
      <rect
        x={-cardWidth / 2}
        y={-cardHeight / 2}
        width={cardWidth}
        height={cardHeight}
        rx={14}
        fill={nodeFill}
        stroke={accentColor}
        strokeWidth={isRootNode ? 1.8 : 1.1}
        opacity={isPremiumLocked ? 0.78 : 1}
        filter="url(#cardShadow)"
      />
      <rect
        x={-cardWidth / 2 + 8}
        y={-cardHeight / 2 + 9}
        width={4}
        height={cardHeight - 18}
        rx={2}
        fill={accentColor}
      />
      <text
        x={-cardWidth / 2 + 20}
        y={statusLabel ? -2 : 4}
        fill={textFill}
        textAnchor="start"
        dominantBaseline="middle"
        stroke="none"
        style={{
          fontSize: isCompact ? '10px' : '12px',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          fontWeight: isRootNode ? 750 : 650,
          pointerEvents: 'none',
          userSelect: 'none',
          textRendering: 'optimizeLegibility',
          fill: textFill,
          stroke: 'none',
          strokeWidth: '0'
        }}
      >
        {displayName}
      </text>
      {statusLabel && (
        <text
          x={cardWidth / 2 - 18}
          y={-cardHeight / 2 + 15}
          fill={statusColor}
          textAnchor="end"
          dominantBaseline="middle"
          style={{
            fontSize: isCompact ? '7px' : '8px',
            fontWeight: 400,
            letterSpacing: 0,
            opacity: 0.82,
            pointerEvents: 'none',
            userSelect: 'none',
          }}
        >
          {statusLabel}
        </text>
      )}
      {!statusLabel && !isRootNode && (
        <text
          x={-cardWidth / 2 + 20}
          y={15}
          fill={isDarkMode ? '#b7bfce' : '#667085'}
          textAnchor="start"
          dominantBaseline="middle"
          style={{
            fontSize: isCompact ? '8px' : '9px',
            fontWeight: 650,
            pointerEvents: 'none',
            userSelect: 'none',
          }}
        >
          Pattern
        </text>
      )}
    </g>
  );
};

// SVG Defs for filters and gradients
const Defs = () => (
  <defs>
    {/* Glow effect */}
    <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
      <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
      <feMerge>
        <feMergeNode in="coloredBlur"/>
        <feMergeNode in="SourceGraphic"/>
      </feMerge>
    </filter>
    <filter id="cardShadow" x="-30%" y="-40%" width="160%" height="180%">
      <feDropShadow dx="0" dy="12" stdDeviation="10" floodColor="#526baa" floodOpacity="0.18"/>
    </filter>
  </defs>
);

// Tree data structure
const treeData = {
  name: 'Arrays & Hashing',
  children: [
    {
      name: 'Two Pointers',
      children: [
        {
          name: 'Binary Search',
          children: [
            { name: 'Sliding Window' },
            { name: 'Stack' }
          ]
        },
        {
          name: 'Linked List',
          children: [
            { name: 'Trees',
              children: [
                { name: 'Trie' },
                { name: 'Heap' }
              ]
            }
          ]
        }
      ]
    },
    {
      name: 'Intervals',
      children: [
        {
          name: 'Greedy',
          children: [
            { name: 'Graphs' },
            { name: 'Dynamic Programming' }
          ]
        },
        {
          name: 'Backtracking',
          children: [
            { name: 'Monotonic Stack' },
            { 
              name: 'Advanced Patterns',
              children: [
                { name: 'Prefix Sum' },
                { name: 'Segment Tree' },
                { name: 'Divide and Conquer' },
                { name: 'Palindromes' },
                { name: 'Sorted List' }
              ]
            }
          ]
        }
      ]
    }
  ]
};

// Reusable Tree Component
export function TreeVisualization({ 
  data = treeData, 
  isCompact = false, 
  showTitle = true, 
  showSchedules = true,
  containerHeight = 'calc(100vh - 120px)',
  treeHeight = { xs: 500, sm: 600, md: 650, lg: 700 }
}) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasActiveSubscription, loading: subscriptionLoading } = useSubscriptionStatus();
  const { isDarkMode } = useTheme();
  
  // Get completion data from localStorage (same as Course component)
  const [completedChapters] = useState(() => {
    try {
      const saved = localStorage.getItem('completedCourseChapters');
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  // Tooltip state
  const [tooltip, setTooltip] = useState({ visible: false });
  
  const getNodeSize = () => {
    if (isCompact) {
      return { x: 90, y: 70 }; // Tighter spacing for compact mode
    }
    return { 
      x: window.innerWidth < 768 ? 120 : window.innerWidth < 1024 ? 140 : 160, 
      y: 80 
    };
  };

  const getTranslate = () => {
    if (isCompact) {
      return { x: 300, y: 100 }; // Center the tree both horizontally and vertically in hero card
    }
    return { 
      x: window.innerWidth < 640 ? window.innerWidth * 0.50 : 
         window.innerWidth < 768 ? window.innerWidth * 0.47 : 
         window.innerWidth < 1024 ? window.innerWidth * 0.42 : 
         window.innerWidth < 1280 ? window.innerWidth * 0.38 : 
         window.innerWidth * 0.35, 
      y: window.innerHeight < 700 ? 120 : window.innerHeight < 900 ? 140 : 160 
    };
  };

  // Show minimal loading screen while subscription status is being determined (only for full roadmap page)
  if (subscriptionLoading && !isCompact) {
    return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: containerHeight,
        bgcolor: 'transparent'
      }}>
        {/* Just empty space - no text to avoid flash */}
      </Box>
    );
  }

  return (
    <Box sx={{ 
      height: isCompact ? '100%' : 'auto',
      minHeight: isCompact ? '100%' : containerHeight, 
      bgcolor: 'transparent', 
      py: isCompact ? 0 : 4, 
      pb: isCompact ? 0 : 10 
    }}>
      {showTitle && (
        <Box sx={{ textAlign: 'center', maxWidth: 820, mx: 'auto', mb: 5, px: 2 }}>
          <Typography
            variant="overline"
            sx={{
              display: 'inline-flex',
              px: 2,
              py: 0.6,
              mb: 2,
              borderRadius: 999,
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              color: 'text.primary',
              fontWeight: 800,
            }}
          >
            Roadmap
          </Typography>
          <Typography variant="h2" sx={{ color: 'text.primary', fontSize: { xs: '2.8rem', md: '4.8rem' }, lineHeight: 0.95, fontWeight: 850 }}>
            A map for the patterns.
          </Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: '1.08rem', lineHeight: 1.7, mt: 2 }}>
            Move from foundation topics to advanced branches with a path that shows dependencies clearly.
          </Typography>
        </Box>
      )}
      <Box sx={{ 
        width: '100%',
        height: isCompact ? '100%' : treeHeight,
        maxWidth: isCompact ? '100%' : { xs: '98%', sm: '95%', md: '90%', lg: '88%', xl: '85%' },
        mx: 'auto',
        border: isCompact ? 'none' : '1px solid', 
        borderColor: 'divider',
        borderRadius: isCompact ? 0 : 5,
        mb: isCompact ? 0 : 4,
        overflow: 'hidden',
        background: isCompact ? 'transparent' : (theme) => theme.palette.background.paper,
        backdropFilter: isCompact ? 'none' : 'blur(18px)',
        boxShadow: isCompact ? 'none' : '0 24px 90px rgba(82, 107, 170, 0.16)',
        '.rd3t-link': {
          stroke: isDarkMode ? '#c7d1ff !important' : '#64748b !important',
          strokeWidth: isCompact ? 1.5 : 2,
          strokeOpacity: 0.6,
          fill: 'none',
          transition: 'all 0.3s ease-in-out',
          '&:hover': {
            strokeOpacity: 1,
            strokeWidth: isCompact ? 2.5 : 3,
          }
        },
        position: 'relative',
        '& .custom-tree-node:hover': {
          '& rect:first-of-type': {
            fill: isDarkMode ? 'rgba(23,32,52,0.98)' : 'rgba(255,255,255,0.98)',
            transform: 'scale(1.05)',
          },
          '& text': {
            fontWeight: 700,
          }
        },
        '& text': {
          color: 'inherit !important',
        },
        '& .rd3t-node text': {
          color: 'inherit !important',
        }
      }}>
        <svg style={{ width: 0, height: 0, position: 'absolute' }}>
          <Defs />
        </svg>
        <Tree
          data={data}
          orientation="vertical"
          renderCustomNodeElement={(props) => CustomNode({ ...props, navigate, user, hasActiveSubscription, isCompact, completedChapters, onTooltip: setTooltip, isDarkMode })}
          pathFunc="diagonal"
          separation={isCompact ? { siblings: 0.6, nonSiblings: 0.8 } : { siblings: 0.8, nonSiblings: 1.0 }}
          translate={getTranslate()}
          nodeSize={getNodeSize()}
          zoomable={!isCompact}
          scaleExtent={{ min: 0.6, max: 1.2 }}
          rootNodeClassName="root-node"
          branchNodeClassName="branch-node"
          leafNodeClassName="leaf-node"
        />
      </Box>

      {/* Tooltip */}
      {tooltip.visible && (
        <Box
          sx={{
            position: 'fixed',
            left: tooltip.x ? tooltip.x - 150 : 0, // Center tooltip on cursor
            top: tooltip.y ? tooltip.y - 80 : 0,
            width: 300,
            bgcolor: 'background.paper',
            border: '2px solid',
            borderColor: tooltip.isCompleted ? 'success.main' : tooltip.isPremiumLocked ? 'warning.main' : 'divider',
            borderRadius: 3,
            p: 2,
            boxShadow: '0 18px 70px rgba(82, 107, 170, 0.22)',
            backdropFilter: 'blur(18px)',
            zIndex: 9999,
            pointerEvents: 'none',
            transform: 'translateY(-100%)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                color: tooltip.isCompleted ? 'success.main' : tooltip.isPremiumLocked ? 'warning.main' : 'text.primary',
                fontWeight: 600,
                mr: 1
              }}
            >
              {tooltip.title}
            </Typography>
            {tooltip.isCompleted && <Typography component="span" sx={{ color: '#15905f', fontWeight: 800 }}>Done</Typography>}
            {tooltip.isPremiumLocked && !tooltip.isCompleted && <Typography component="span" sx={{ color: '#b7791f', fontWeight: 800 }}>Pro</Typography>}
          </Box>
          <Typography 
            variant="body2" 
            sx={{ 
              color: 'text.secondary',
              lineHeight: 1.4,
              fontSize: '0.9rem'
            }}
          >
            {tooltip.content}
          </Typography>
          {tooltip.isCompleted && (
            <Typography 
              variant="caption" 
              sx={{ 
                color: '#15905f',
                fontWeight: 500,
                mt: 1,
                display: 'block'
              }}
            >
              ✓ Completed
            </Typography>
          )}
          {tooltip.isPremiumLocked && !tooltip.isCompleted && (
            <Typography 
              variant="caption" 
              sx={{ 
                color: '#b7791f',
                fontWeight: 500,
                mt: 1,
                display: 'block'
              }}
            >
              Premium content
            </Typography>
          )}
        </Box>
      )}

      {showSchedules && <RoadmapGuidance />}
    </Box>
  );
}

const guidanceCardSx = {
  bgcolor: 'background.paper',
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 5,
  p: { xs: 3, md: 4 },
  boxShadow: '0 24px 80px rgba(82, 107, 170, 0.12)',
  backdropFilter: 'blur(18px)',
};

const labelSx = {
  display: 'inline-flex',
  px: 1.4,
  py: 0.5,
  borderRadius: 999,
  bgcolor: 'action.hover',
  color: 'text.secondary',
  fontSize: '0.72rem',
  fontWeight: 750,
  mb: 1.5,
};

function RoadmapGuidance() {
  const approaches = [
    {
      label: 'Level order',
      title: 'Breadth-first',
      body: 'Move across one dependency level at a time. This is the cleanest path for building stable foundations before advanced branches.',
      steps: ['Foundation: Arrays and Hashing', 'Core: Two Pointers, Binary Search, Sliding Window', 'Structures: Stack, Linked List, Trees', 'Advanced: Graphs, DP, Backtracking'],
    },
    {
      label: 'Branch order',
      title: 'Depth-first',
      body: 'Choose a branch and follow it until it naturally ends. This works well when you want concentrated practice on one family of patterns.',
      steps: ['Array branch: Hashing to Sliding Window', 'Structure branch: Linked List to Trees', 'Optimization branch: Intervals to Greedy', 'Search branch: Backtracking to Advanced Patterns'],
    },
    {
      label: 'Recommended',
      title: 'Hybrid',
      body: 'Use breadth-first for the first pass, then depth-first on weak branches. It keeps momentum high without leaving large gaps.',
      steps: ['Finish the first two levels', 'Pick the weakest branch', 'Mix quizzes and flashcards after each branch', 'Return to the roadmap weekly'],
    },
  ];

  const schedules = [
    {
      title: 'Foundation pace',
      duration: '8 weeks',
      items: ['Weeks 1-2: Arrays, Hashing, Two Pointers', 'Weeks 3-4: Binary Search, Sliding Window, Stack', 'Weeks 5-6: Linked List, Trees, Heap', 'Weeks 7-8: Graphs, DP, review'],
    },
    {
      title: 'Interview ramp',
      duration: '4 weeks',
      items: ['Week 1: Core array and search patterns', 'Week 2: Structures and traversal', 'Week 3: Backtracking, Graphs, DP', 'Week 4: Mixed mocks and weak spots'],
    },
    {
      title: 'Final review',
      duration: '2 weeks',
      items: ['Days 1-3: redo missed problems', 'Days 4-7: timed mixed sets', 'Days 8-11: systemize templates', 'Days 12-14: mocks and recovery'],
    },
  ];

  return (
    <Box sx={{ maxWidth: 1240, mx: 'auto', px: { xs: 2, sm: 3, md: 5 }, mt: 7, mb: 8 }}>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="overline" sx={labelSx}>Learning strategy</Typography>
        <Typography variant="h3" sx={{ color: 'text.primary', fontSize: { xs: '2.1rem', md: '3.4rem' }, fontWeight: 850, lineHeight: 1 }}>
          Pick the traversal that matches your timeline.
        </Typography>
        <Typography sx={{ color: 'text.secondary', mt: 1.5, fontSize: '1.02rem' }}>
          The roadmap now uses the same visual language as the rest of the product: quiet cards, clear hierarchy, and practical study paths.
        </Typography>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(3, 1fr)' }, gap: 2.5, mb: 6 }}>
        {approaches.map((approach, index) => (
          <Box key={approach.title} sx={guidanceCardSx}>
            <Typography variant="overline" sx={labelSx}>{String(index + 1).padStart(2, '0')} / {approach.label}</Typography>
            <Typography variant="h5" sx={{ color: 'text.primary', fontWeight: 800, mb: 1 }}>{approach.title}</Typography>
            <Typography sx={{ color: 'text.secondary', lineHeight: 1.65, mb: 2.5 }}>{approach.body}</Typography>
            <Box sx={{ display: 'grid', gap: 1 }}>
              {approach.steps.map((step) => (
                <Box key={step} sx={{ p: 1.4, borderRadius: 3, bgcolor: 'action.hover', border: '1px solid', borderColor: 'divider', color: 'text.primary', fontSize: '0.92rem', fontWeight: 650 }}>
                  {step}
                </Box>
              ))}
            </Box>
          </Box>
        ))}
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '0.9fr 1.1fr 1.1fr' }, gap: 2.5 }}>
        {schedules.map((schedule) => (
          <Box key={schedule.title} sx={guidanceCardSx}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'center', mb: 2 }}>
              <Typography variant="h5" sx={{ color: 'text.primary', fontWeight: 800 }}>{schedule.title}</Typography>
              <Typography sx={{ px: 1.5, py: 0.5, borderRadius: 999, bgcolor: '#0a0d14', color: '#fff', fontSize: '0.78rem', fontWeight: 800 }}>
                {schedule.duration}
              </Typography>
            </Box>
            <Box sx={{ display: 'grid', gap: 1.2 }}>
              {schedule.items.map((item) => (
                <Box key={item} sx={{ display: 'flex', gap: 1.2, color: 'text.secondary', lineHeight: 1.5 }}>
                  <Box sx={{ mt: '0.55em', width: 6, height: 6, flex: '0 0 auto', borderRadius: '50%', bgcolor: 'secondary.main' }} />
                  <Typography sx={{ fontSize: '0.94rem' }}>{item}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function RoadmapTree() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return <TreeVisualization data={treeData} />;
}

export default RoadmapTree;
